/**
 * recistPanel.js — окно «RECIST: динамика» в режиме сравнения. Тонкая обвязка
 * над чистым ядром core/recist.js (правила и пороги проверены на бумажных
 * случаях в tests/recist_test.mjs); здесь — журнал замеров и таблица.
 *
 * ОТКУДА ЧИСЛА. Замеры вводит ВРАЧ: либо руками (дата, имя очага, мм, узел),
 * либо кнопкой «добавить последнюю меру» — она берёт эквивалентные диаметры из
 * «Измерить очаг» сразу для обеих дат. Про эквивалентный диаметр окно говорит
 * прямо: RECIST формально меряет ДЛИННИК (а у узлов — короткую ось), диаметр
 * шара того же объёма — приближение; для строгого протокола впишите длинник
 * руками.
 *
 * ХРАНЕНИЕ. Журнал лежит в localStorage по PatientID — переживает перезапуск,
 * не тащит новых таблиц в базу. Ключ: mikhmel.recist.<patientId>.
 *
 * ЧЕГО ОКНО НЕ ДЕЛАЕТ (и говорит об этом): не оценивает нецелевые очаги
 * (кроме флага «новые»), не подтверждает CR/PR повторным исследованием через
 * 4 недели — это правило времени, а не арифметики.
 */

import { оценкаRECIST, проверитьЦели } from '../core/recist.js';
import { окно, строкаВвода } from './quantTools.js';
import { showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';

const _ключ = (pid) => `mikhmel.recist.${pid || 'без-id'}`;

function _читать(pid) {
  try { return JSON.parse(localStorage.getItem(_ключ(pid)) || '{"замеры":[],"новые":{}}'); }
  catch (e) { return { замеры: [], новые: {} }; }
}
function _писать(pid, ж) {
  try { localStorage.setItem(_ключ(pid), JSON.stringify(ж)); return true; }
  catch (e) { log.warn('[RECIST] журнал не записался:', e.message); return false; }
}

const _датаЧел = (д) => (/^\d{8}$/.test(д) ? `${д.slice(6, 8)}.${д.slice(4, 6)}.${д.slice(0, 4)}` : д);

/** Точки для ядра из плоского журнала. */
function _точки(ж) {
  const поДатам = new Map();
  for (const з of ж.замеры || []) {
    if (!з?.дата || !з?.имя || !Number.isFinite(з.мм)) continue;
    if (!поДатам.has(з.дата)) поДатам.set(з.дата, []);
    поДатам.get(з.дата).push({ имя: з.имя, мм: з.мм, узел: !!з.узел });
  }
  return [...поДатам.entries()]
    .map(([дата, очаги]) => ({ дата, очаги, новыеОчаги: !!ж.новые?.[дата] }))
    .sort((а, б) => а.дата.localeCompare(б.дата));
}

/**
 * @param {object} ктх — { patientId, patientName, даты: [{дата, подпись}],
 *   последняяМера: () => null | { записи: [{дата, мм}], откуда: string } }
 */
export function открытьRECIST(ктх = {}) {
  const pid = ктх.patientId || '';
  const ж = _читать(pid);
  const w = окно(`RECIST: динамика — ${ктх.patientName || pid || 'пациент'}`, { ширина: 760 });

  const форма = document.createElement('div');
  const датыИзв = (ктх.даты || []).map((д) => д.дата).filter(Boolean);
  форма.innerHTML = строкаВвода('Дата (ГГГГММДД)', 'rc-дата', датыИзв[датыИзв.length - 1] || '', 'style="width:110px"')
    + строкаВвода('Очаг («орган-номер», напр. печень-1)', 'rc-имя', '', 'style="width:150px"')
    + строкаВвода('Размер, мм (узел — короткая ось)', 'rc-мм', '', 'style="width:80px"')
    + `<label style="display:flex;align-items:center;gap:8px;margin:5px 0;">
         <span style="min-width:210px;color:#9fb3cf;">Это лимфоузел</span>
         <input type="checkbox" id="rc-узел"></label>
       <label style="display:flex;align-items:center;gap:8px;margin:5px 0;">
         <span style="min-width:210px;color:#9fb3cf;">На этой дате появились НОВЫЕ очаги</span>
         <input type="checkbox" id="rc-новые"></label>`;
  const кнопки = document.createElement('div');
  кнопки.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap;margin:6px 0;';
  const таблица = document.createElement('div');
  w.тело.append(форма, кнопки, таблица);
  w.тело.insertAdjacentHTML('beforeend',
    '<div style="margin-top:10px;padding:8px 10px;border:1px solid #5a4a1e;border-radius:8px;background:#241d0c;color:#e8d48b;">'
    + 'Исследовательский расчёт, не для выбора лечения. Окно оценивает ТОЛЬКО целевые очаги '
    + '(нецелевые — лишь флагом «новые») и не подтверждает CR/PR повторным исследованием. '
    + 'Диаметр из «Измерить очаг» — эквивалент по объёму, а не длинник RECIST: для строгого протокола впишите длинник руками.</div>');

  const $ = (id) => document.getElementById(id);

  const перерисовать = () => {
    const точки = _точки(ж);
    if (!точки.length) { таблица.innerHTML = '<div style="color:#9fb3cf;margin:8px 0;">Журнал пуст: добавьте замеры очагов по датам.</div>'; return; }
    const имена = [...new Set(точки.flatMap((т) => т.очаги.map((о) => о.имя)))];
    const р = оценкаRECIST(точки);
    let html = '<table style="width:100%;border-collapse:collapse;font-size:12px;margin-top:6px;">'
      + '<tr><th style="text-align:left;padding:3px 6px;color:#9fb3cf;">Дата</th>'
      + имена.map((и) => `<th style="padding:3px 6px;color:#9fb3cf;">${и}</th>`).join('')
      + '<th style="padding:3px 6px;color:#9fb3cf;">Σ, мм</th><th style="padding:3px 6px;color:#9fb3cf;">от базы</th><th style="padding:3px 6px;color:#9fb3cf;">Ответ</th></tr>';
    const цвет = { CR: '#7ee2a8', PR: '#a8d7ff', SD: '#e8d48b', PD: '#f2a0a0' };
    for (let i = 0; i < точки.length; i++) {
      const т = точки[i];
      const стр = р.ok ? р.поДатам.find((x) => x.дата === т.дата) : null;
      html += `<tr style="border-top:1px solid #223047;"><td style="padding:3px 6px;">${_датаЧел(т.дата)}${ж.новые?.[т.дата] ? ' <span title="новые очаги">🆕</span>' : ''}</td>`
        + имена.map((и) => {
          const о = т.очаги.find((x) => x.имя === и);
          return `<td style="text-align:center;padding:3px 6px;">${о ? `${о.мм}${о.узел ? ' у' : ''}` : '—'}</td>`;
        }).join('')
        + `<td style="text-align:center;padding:3px 6px;">${стр ? стр.сумма.toFixed(1) : '—'}</td>`
        + `<td style="text-align:center;padding:3px 6px;">${стр && i ? (стр.отБазыПроц > 0 ? '+' : '') + стр.отБазыПроц.toFixed(0) + '%' : '—'}</td>`
        + `<td style="text-align:center;padding:3px 6px;color:${цвет[стр?.ответ] || '#e7eefc'};" title="${стр?.почему || ''}">${стр?.ответ || '—'}</td></tr>`;
    }
    html += '</table>';
    if (р.ok) {
      html += `<div style="margin-top:6px;color:#9fb3cf;">Целевые: ${р.цели.join(', ')} · базовая сумма ${р.базоваяСумма.toFixed(1)} мм.`
        + (р.выбор.отвергнутые.length ? `<br>Не в целях: ${р.выбор.отвергнутые.map((х) => `${х.имя} (${х.почему})`).join('; ')}.` : '')
        + (р.выбор.предупреждения.length ? `<br>${р.выбор.предупреждения.join(' ')}` : '') + '</div>';
    } else {
      html += `<div style="margin-top:6px;color:#f2b8b5;">Оценка не считается: ${р.причина}</div>`;
    }
    таблица.innerHTML = html;
  };

  const кн = (текст, действие, главная = false) => {
    const б = document.createElement('button');
    б.textContent = текст;
    б.style.cssText = главная
      ? 'background:#1d3a2a;border:1px solid #2f5c43;color:#d7f5e3;border-radius:8px;padding:6px 12px;cursor:pointer;'
      : 'background:#1d2939;border:1px solid #33415a;color:#e7eefc;border-radius:8px;padding:6px 12px;cursor:pointer;';
    б.addEventListener('click', действие);
    кнопки.appendChild(б);
  };

  кн('Добавить замер', () => {
    const дата = String($('rc-дата')?.value || '').trim();
    const имя = String($('rc-имя')?.value || '').trim();
    const мм = Number(String($('rc-мм')?.value || '').replace(',', '.'));
    if (!/^\d{8}$/.test(дата)) { showToast('Дата — восемь цифр: ГГГГММДД', 'warning', 4000); return; }
    if (!имя || !Number.isFinite(мм) || мм < 0) { showToast('Нужны имя очага и размер в мм', 'warning', 4000); return; }
    ж.замеры = (ж.замеры || []).filter((з) => !(з.дата === дата && з.имя === имя));   // тот же очаг той же даты — правка, не дубль
    ж.замеры.push({ дата, имя, мм, узел: !!$('rc-узел')?.checked });
    if ($('rc-новые')?.checked) { ж.новые = ж.новые || {}; ж.новые[дата] = true; }
    if (!_писать(pid, ж)) showToast('Журнал НЕ сохранился (хранилище браузера)', 'error', 6000);
    перерисовать();
  }, true);

  if (typeof ктх.последняяМера === 'function') {
    кн('Добавить последнюю меру очага (обе даты)', () => {
      const м = ктх.последняяМера();
      if (!м?.записи?.length) { showToast('Сначала «Измерить очаг» в полоске сравнения', 'warning', 5000); return; }
      const имя = String($('rc-имя')?.value || '').trim();
      if (!имя) { showToast('Впишите имя очага («орган-номер») — под ним лягут обе даты', 'warning', 5000); return; }
      for (const з of м.записи) {
        if (!/^\d{8}$/.test(String(з.дата)) || !Number.isFinite(з.мм)) continue;
        ж.замеры = (ж.замеры || []).filter((x) => !(x.дата === з.дата && x.имя === имя));
        ж.замеры.push({ дата: з.дата, имя, мм: Math.round(з.мм * 10) / 10, узел: !!$('rc-узел')?.checked });
      }
      if (!_писать(pid, ж)) showToast('Журнал НЕ сохранился (хранилище браузера)', 'error', 6000);
      showToast(`Очаг «${имя}»: добавлены ${м.записи.length} даты (${м.откуда})`, 'success', 5000);
      перерисовать();
    });
  }

  кн('Удалить очаг…', () => {
    const имя = String($('rc-имя')?.value || '').trim();
    if (!имя) { showToast('Впишите имя очага, который убрать', 'warning', 4000); return; }
    const было = (ж.замеры || []).length;
    ж.замеры = (ж.замеры || []).filter((з) => з.имя !== имя);
    _писать(pid, ж);
    showToast(`Убрано замеров: ${было - ж.замеры.length}`, 'info', 3000);
    перерисовать();
  });

  кн('Очистить журнал пациента', () => {
    if (!confirm('Стереть ВСЕ замеры RECIST этого пациента? Файлы исследований не трогаются.')) return;
    ж.замеры = []; ж.новые = {};
    _писать(pid, ж);
    перерисовать();
  });

  перерисовать();
}
